import PageLayout from "components/layout/PageLayout";
import { useTranslation } from "react-i18next";
import { useDeleteHealthProfileMutation, useGetHealthProfilesQuery, useSaveHealthProfileMutation } from "./api/healtProfilesApiSlice";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { useState } from "react";
import { Dialog } from "primereact/dialog";
import AvatarMini from "components/commons/AvatarMini";
import HealthProfileForm from "./components/HealthProfileForm";
import HasPermission, { CanManageHealthPolicy } from "app/PermissionChecker";

const HealthProfiles = () => {
    const [t] = useTranslation(["global", "health"]);
    const canManageHealthPolicy = HasPermission(CanManageHealthPolicy)
    const { data: profiles, isFetching, isSuccess, refetch } = useGetHealthProfilesQuery()



    const nameTemplate = (profile) => (
        profile.profile.isDefault ? <span className="text-muted">Default</span> : profile.profile.name
    );

    const patientsTemplate = (profile) => (
        profile.profile.isDefault ? "-" : <PatientsTemplate data={profile} />
    );

    const hrSafeZoneTemplate = (profile) => {
        const hrSafeZone = profile.profile.features.find(f => f.name === "HR-SAFEZONE");
        const hrEnabled = hrSafeZone?.enabled || false;

        return (
            <div className="row bg-dark p-2 rounded-pill">
                <div className="col">
                    <div className="form-check form-switch">
                        <input readOnly className="form-check-input" type="checkbox" checked={hrEnabled} />
                    </div>
                </div>
                <div className="col text-secondary">
                    {hrEnabled ? hrSafeZone.settings?.min : "-"}
                </div>
                <div className="col text-secondary">
                    {hrEnabled ? hrSafeZone.settings?.max : "-"}
                </div>
            </div>
        );
    };

    const fallDetectionTemplate = (profile) => {
        const fallDetection = profile.profile.features.find(f => f.name === "FALL-DETECTION");
        return (
            <div className="row bg-dark p-2 rounded-pill">
                <div className="col">
                    <div className="form-check form-switch">
                        <input readOnly className="form-check-input" type="checkbox" checked={fallDetection?.enabled || false} />
                    </div>
                </div>
                <div className="col text-secondary">
                    {fallDetection.enabled ? <>{fallDetection.settings?.threshold}</> : "-"}
                </div>
            </div>
        );
    };

    const inactivtyTemplate = (profile) => {
        const inactivity = profile.profile.features.find(f => f.name === "INACTIVITY");
        return (
            <div className="row bg-dark p-2 rounded-pill">
                <div className="col">
                    <div className="form-check form-switch">
                        <input readOnly className="form-check-input" type="checkbox" checked={inactivity.enabled} />
                    </div>
                </div>
                <div className="col text-secondary">
                    {inactivity.enabled ? <>{inactivity.settings?.threshold}</> : "-"}
                </div>
            </div>
        );
    };

    const trackingTemplatev1 = (profile) => {
        const hrTracking = profile.profile.features.find(f => f.name === "HR");
        const stepsTracking = profile.profile.features.find(f => f.name === "STEPS");
        return (
            <div>
                <div className="form-check form-switch">
                    <label className="form-check-label small "><i className="fa-solid fa-heart-pulse opacity-50"></i> HR</label>
                    <input readOnly className="form-check-input" type="checkbox" checked={hrTracking?.enabled || false} />
                </div>
                <div className="form-check form-switch">
                    <label className="form-check-label small"> <i className="fa-solid fa-shoe-prints opacity-50"></i> Steps</label>
                    <input readOnly className="form-check-input" type="checkbox" checked={stepsTracking?.enabled || false} />
                </div>
            </div>
        );
    };


    const trackingTemplate = (profile) => {
        const hrTracking = profile.profile.features.find(f => f.name === "HR");
        const stepsTracking = profile.profile.features.find(f => f.name === "STEPS");
        const tempTracking = profile.profile.features.find(f => f.name === "TEMPS");
        const vo2MaxTracking = profile.profile.features.find(f => f.name === "VO2MAX");
        return (
            <div>
                <div className="row p-2">
                    <div className={`col ${hrTracking?.enabled != true && "opacity-25"}`}>
                        <h6><i className="fa-solid fa-heart-pulse"></i></h6>
                        <small>bpm</small>
                    </div>
                    <div className={`col ${stepsTracking?.enabled != true && "opacity-25"}`}>
                        <h6><i className="fa-solid fa-shoe-prints"></i></h6>
                        <small>{t("health:unit.steps")}</small>
                    </div>
                    <div className={`col ${tempTracking?.enabled != true && "opacity-25"}`}>
                        <h6><i className="fa-solid fa-temperature-three-quarters"></i></h6>
                        <small>°C</small>
                    </div>
                    <div className={`col ${vo2MaxTracking?.enabled != true && "opacity-25"}`}>
                        <h6><i className="fa-solid fa-lungs"></i></h6>
                        <small>%</small>
                    </div>
                </div>
            </div>
        );
    };

    const safeGuardHeader = (featureName, icon) => {
        return (
            <div>
                <span className="text-eh small">SafeGuard</span><br />
                {icon} {featureName}
            </div>
        )
    }

    const dataTrackingHeader = () => {
        return (
            <div>
                <span className="text-secondary small">Data</span><br />
                <i className="fa-solid fa-chart-line opacity-50"></i> Tracking
            </div>
        )
    }

    const emergencyNumberHeader = () => {
        return (
            <div>
                <span className="text-danger small">Emergency</span><br />
                <i className="fa-solid fa-phone opacity-50"></i>  Phone Number
            </div>
        )
    }


    const emergencyPhoneTemplate = (profile) => profile.profile.emergencyNumber;

    const actionTemplate = (profile) => {
        return (
            <div>
                <EditHealthProfileDialog data={profile} />
                <DeleteProfileTemplate data={profile} />
            </div>
        )
    };

    let content;
    if (isSuccess) {
        content = (
            <div>
                {
                    canManageHealthPolicy &&
                    <div className=" text-end"><NewHealthProfileDialog /></div>
                }
                <DataTable
                    dataKey="profile.guid"
                    value={profiles}
                    paginator
                    rows={25}
                    stripedRows
                    tableStyle={{ minWidth: '50rem' }}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                >
                    <Column field="profile.name" header={t("property.name")} body={nameTemplate} />
                    <Column field="profile.features" header={safeGuardHeader("HR SafeZone", <i className="fa-solid fa-heart-pulse opacity-50"></i>)} body={hrSafeZoneTemplate} />
                    <Column field="profile.features" header={safeGuardHeader(t("health:entity.profile.features.FALL-DETECTION_short"), <i className="fa-solid fa-person-falling opacity-50"></i>)} body={fallDetectionTemplate} />
                    <Column field="profile.features" header={safeGuardHeader(t("health:entity.profile.features.INACTIVITY_short"), <i className="fa-solid fa-notes-medical opacity-50"></i>)} body={inactivtyTemplate} />
                    <Column field="profile.features" header={dataTrackingHeader} body={trackingTemplate} />
                    <Column field="profile.features" header={emergencyNumberHeader} body={emergencyPhoneTemplate} />
                    <Column field="profile.patients" header={<i className="fa-solid fa-users"></i>} body={patientsTemplate} />
                    {
                        canManageHealthPolicy &&
                        <Column field="profile.patients" header={t("property.actions")} body={actionTemplate} />
                    }

                </DataTable>

            </div>

        );
    }

    return (
        <PageLayout
            pageTitle={"Health Profiles"}
            isLoadingContent={isFetching}
            onPageRefresh={refetch}
            bodyContent={content}
        />
    );
};

export default HealthProfiles;



const NewHealthProfileDialog = () => {
    const [t] = useTranslation(["global", "health"]);
    const [openDialog, setOpenDialog] = useState(false);
    const [createProfile, { isLoading }] = useSaveHealthProfileMutation();

    const handleNewProfile = async (payload) => {
        await createProfile(payload);
        setOpenDialog(false);
    };

    return (
        <>
            <button className="btn btn-primary" onClick={() => setOpenDialog(true)}> + {t("action.add")}</button>
            <Dialog header={t("health:actions.new_profile")} visible={openDialog} onHide={() => setOpenDialog(false)} style={{ width: '20vw' }}>
                <HealthProfileForm
                    onSave={handleNewProfile}
                    onCancel={() => setOpenDialog(false)}
                    isEditMode={false}
                />
            </Dialog>
        </>
    );
};

const EditHealthProfileDialog = ({ data }) => {
    const [t] = useTranslation(["global", "health"]);
    const [openDialog, setOpenDialog] = useState(false);
    const [saveProfile, { isLoading }] = useSaveHealthProfileMutation();

    const handleEditProfile = async (payload) => {
        await saveProfile(payload);
        setOpenDialog(false);
    };
    return (
        <>
            <button className="btn btn-light" onClick={() => setOpenDialog(true)}><i className="fa-solid fa-pen"></i></button>
            <Dialog header={t("health:actions.edit_profile")} visible={openDialog} onHide={() => setOpenDialog(false)} style={{ width: '20vw' }}>
                <HealthProfileForm
                    initialData={data.profile}
                    onSave={handleEditProfile}
                    onCancel={() => setOpenDialog(false)}
                    isEditMode={true}
                />
            </Dialog>
        </>
    );
};

const DeleteProfileTemplate = ({ data }) => {
    const [t] = useTranslation(["global", "health"]);
    const [openDialog, setOpenDialog] = useState(false);
    const [deleteProfile] = useDeleteHealthProfileMutation()
    return (
        <>
            <button className="btn btn-danger m-1" onClick={() => setOpenDialog(true)}><i className="fa-solid fa-trash"></i></button>
            <Dialog header={t("health:actions.delete_profile")} visible={openDialog} onHide={() => setOpenDialog(false)} style={{ width: '20vw' }}>
                <span>{t("health:helper.profile_delete_confirm")} <b>{data.profile.name}</b> ?</span>
                <div className="text-end mt-4">
                    <button className="btn btn-danger mx-2" onClick={() => {
                        deleteProfile(data.profile.guid)
                        setOpenDialog(false)
                    }}>  {t("action.delete")} </button>
                    <button className="btn btn-secondary" onClick={() => setOpenDialog(false)}>{t("action.cancel")}</button>
                </div>

            </Dialog>
        </>
    );
};

const PatientsTemplate = ({ data }) => {
    const [t] = useTranslation(["global", "health"]);
    const [openDialog, setOpenDialog] = useState(false);
    return (
        <>
            <button className="btn btn-secondary m-1" onClick={() => setOpenDialog(true)}>
                <i className="fa-solid fa-user"></i> {data.patients.length}
            </button>
            <Dialog visible={openDialog} onHide={() => setOpenDialog(false)}>
                <h4>{data.profile.name}</h4>
                <hr />
                <div className="p-2">{t("health:helper.assigned_to")}</div>
                {data.patients.map(p => (
                    <AvatarMini displayName={p.displayName} guid={p.guid} key={p.guid} destination="/dashboard/health/history" />
                ))}
            </Dialog>
        </>
    );
};
